import React from 'react';
import {
  Grid,
  Slider,
  sliderClasses,
  Stack,
  styled,
  Typography,
} from '@mui/material';

export interface LevelBarProps {
  optimalMin: number;
  optimalMax: number;
  level: number;
  detail?: boolean;
}

export const RootineLevelBar = (props: LevelBarProps): JSX.Element => {
  const colors = {
    red: '#EF562B',
    orange: 'orange',
    yellow: '#FFD233',
    green: '#2AD033',
    blue: '#0075FF',
  };

  const backgroundGradient = (): string => {
    if (props.optimalMin === 0) {
      return `linear-gradient(90deg,${colors.green},${colors.green},${colors.green},${colors.green},${colors.yellow},${colors.orange},${colors.red})`;
    } else {
      return `linear-gradient(90deg,${colors.red},${colors.orange},${colors.yellow},${colors.green},${colors.green},${colors.green},${colors.yellow},${colors.orange},${colors.red})`;
    }
  };

  const thumbColor = (): string => {
    return props.level >= props.optimalMin && props.level <= props.optimalMax
      ? colors.green
      : colors.red;
  };

  const levelString = (): string => {
    if (props.level < props.optimalMin) {
      return 'Low';
    } else if (props.level > props.optimalMax) {
      return 'High';
    } else {
      return 'Optimal';
    }
  };

  const LevelBar = styled(Slider)(({ theme }) => ({
    height: props.detail ? '10px' : '5px',
    [`& .${sliderClasses.rail}`]: {
      background: backgroundGradient(),
      opacity: 1.0,
    },
    [`& .${sliderClasses.track}`]: {
      background: 'none',
      border: 'none',
    },
    [`& .${sliderClasses.thumb}`]: {
      size: 'small',
      color: thumbColor(),
      border: '2px solid white',
      hover: { cursor: 'auto' },
    },
  }));

  const getPreciseValue = (): number => {
    const optimalRange = props.optimalMax - props.optimalMin;
    const zeroBasedMin = props.optimalMin === 0;
    // level is between 0 and optimal min
    if (props.level < props.optimalMin) {
      const ratio = props.level / props.optimalMin;
      return ratio * 33;
    }
    // level is optimal
    else if (props.level <= props.optimalMax) {
      const ratio = (props.level - props.optimalMin) / optimalRange;
      return zeroBasedMin ? ratio * 60 : 33 + ratio * 33;
    }
    // level is high
    else {
      const ratio = (props.level - props.optimalMax) / optimalRange;
      return zeroBasedMin ? 60 + ratio * 40 : 66 + ratio * 33;
    }
  };

  const levelBarProps = {
    max: 100,
    min: 0,
    value: getPreciseValue(),
  };

  return (
    <>
      {props.detail ? (
        <Stack alignItems="center">
          <Typography fontSize="16px" color={thumbColor()}>
            {levelString()}
          </Typography>
          <Typography fontSize="36px" color={thumbColor()}>
            {Math.round(props.level * 100) / 100}
          </Typography>
          <LevelBar classes={{ thumbSizeSmall: 'small' }} {...levelBarProps} />
        </Stack>
      ) : (
        <Grid container item xs={12} sx={{ px: { xs: 1 } }}>
          <Grid item xs={12}>
            <Typography
              textAlign="center"
              color={thumbColor()}
              sx={{ textAlign: { xs: 'right', md: 'center' } }}
            >
              {levelString()}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <LevelBar {...levelBarProps} />
          </Grid>
        </Grid>
      )}
    </>
  );
};
